/**
 * Store Design System — the axes that, together with a color palette,
 * define a storefront's entire personality.
 *
 * The palette (50 themes) lives in `storeThemes.js`. This module adds the
 * *structural* dimensions a designer actually tweaks: typography, corner
 * language, spatial density, product-card anatomy, cover treatment, surface
 * texture, motion energy and button shape.
 *
 * A store persists a compact `design_config`:
 *   { themeId, fontPair, radius, density, card, cover, texture, motion, button, vibe }
 *
 * `resolveDesign()` fills the gaps (and migrates a legacy bare `theme_id`),
 * `VIBES` are one-tap personalities that compose every axis at once.
 *
 * Nothing here touches the DOM — `applyStoreDesign.js` turns a resolved
 * config into CSS variables + data attributes.
 */

import { DEFAULT_STORE_THEME_ID, findStoreTheme } from './storeThemes';

// ───────────────────────────── typography ─────────────────────────────
// Pairs use fonts already loaded in index.html. Each: a display face for
// the store name / headings and a body face for everything else. All faces
// render Hebrew. `tracking` / `displayWeight` tune the headline character.

export const FONT_PAIRS = [
    { id: 'clean',     label: 'נקי ומודרני',   display: "'Assistant'",          body: "'Assistant'",     displayWeight: 800, tracking: '-0.01em' },
    { id: 'editorial', label: 'מגזין יוקרתי',   display: "'Frank Ruhl Libre'",   body: "'Heebo'",         displayWeight: 700, tracking: '0' },
    { id: 'grand',     label: 'מונומנטלי',      display: "'Suez One'",           body: "'Assistant'",     displayWeight: 400, tracking: '-0.005em' },
    { id: 'soft',      label: 'עגול וידידותי',  display: "'Varela Round'",       body: "'Varela Round'",  displayWeight: 400, tracking: '0' },
    { id: 'bold',      label: 'נועז וכבד',      display: "'Heebo'",              body: "'Rubik'",         displayWeight: 900, tracking: '-0.02em' },
    { id: 'statement', label: 'הצהרתי',         display: "'Secular One'",        body: "'Heebo'",         displayWeight: 400, tracking: '0' },
    { id: 'poster',    label: 'פוסטר רחוב',     display: "'Karantina'",          body: "'Rubik'",         displayWeight: 700, tracking: '0.01em' },
    { id: 'handcraft', label: 'כתב יד אמן',     display: "'Amatic SC'",          body: "'Assistant'",     displayWeight: 700, tracking: '0.01em' },
];

export const findFontPair = (id) => FONT_PAIRS.find(f => f.id === id) || FONT_PAIRS[0];

// ───────────────────────────── corner language ────────────────────────
// Drives a remapped radius ladder on the storefront (see applyStoreDesign).

export const RADII = [
    { id: 'sharp',   label: 'חד וזוויתי',  scale: 0,    base: '0px' },
    { id: 'soft',    label: 'רך',          scale: 0.55, base: '0.5rem' },
    { id: 'rounded', label: 'מעוגל',       scale: 1,    base: '1rem' },
    { id: 'pill',    label: 'גלולה',       scale: 1.7,  base: '1.75rem' },
];
export const findRadius = (id) => RADII.find(r => r.id === id) || RADII[2];

// ───────────────────────────── spatial density ────────────────────────
// A single multiplier scales paddings / gaps on the storefront shell.

export const DENSITIES = [
    { id: 'compact',  label: 'צפוף',   factor: 0.82 },
    { id: 'cozy',     label: 'מאוזן',  factor: 1 },
    { id: 'spacious', label: 'מרווח',  factor: 1.32 },
];
export const findDensity = (id) => DENSITIES.find(d => d.id === id) || DENSITIES[1];

// ───────────────────────────── product card anatomy ───────────────────
export const CARD_STYLES = [
    { id: 'classic',   label: 'קלאסי',   hint: 'תמונה למעלה, פרטים למטה' },
    { id: 'editorial', label: 'מגזין',   hint: 'תמונה מלאה, טקסט על גבי שכבה' },
    { id: 'minimal',   label: 'מינימלי', hint: 'ללא מסגרת, רוח לנשום' },
    { id: 'framed',    label: 'ממוסגר',  hint: 'מסגרת מודגשת, מוזיאלי' },
];
export const findCard = (id) => CARD_STYLES.find(c => c.id === id) || CARD_STYLES[0];

// ───────────────────────────── cover treatment ────────────────────────
export const COVER_STYLES = [
    { id: 'hero',    label: 'כותרת ענקית', hint: 'תמונה מלאה עם שכבת כיתוב' },
    { id: 'banner',  label: 'באנר',        hint: 'רצועה רחבה ונקייה' },
    { id: 'split',   label: 'מפוצל',       hint: 'תמונה לצד שם החנות' },
    { id: 'minimal', label: 'מינימלי',     hint: 'ללא תמונה, שם בלבד' },
];
export const findCover = (id) => COVER_STYLES.find(c => c.id === id) || COVER_STYLES[0];

// ───────────────────────────── surface texture ────────────────────────
// Rendered as a faint, fixed overlay tinted by the palette ink.
export const TEXTURES = [
    { id: 'none',  label: 'חלק' },
    { id: 'paper', label: 'נייר' },
    { id: 'dots',  label: 'נקודות' },
    { id: 'grid',  label: 'רשת' },
    { id: 'mesh',  label: 'ערפל צבע' },
];
export const findTexture = (id) => TEXTURES.find(t => t.id === id) || TEXTURES[0];

// ───────────────────────────── motion energy ──────────────────────────
export const MOTIONS = [
    { id: 'calm',  label: 'רגוע',  dur: '320ms', lift: '0px',   scale: '1.00' },
    { id: 'lively', label: 'חי',   dur: '200ms', lift: '-4px',  scale: '1.03' },
];
export const findMotion = (id) => MOTIONS.find(m => m.id === id) || MOTIONS[0];

// ───────────────────────────── button shape ───────────────────────────
export const BUTTON_STYLES = [
    { id: 'solid',   label: 'מלא' },
    { id: 'pill',    label: 'גלולה' },
    { id: 'outline', label: 'קווי' },
    { id: 'sharp',   label: 'חד' },
];
export const findButton = (id) => BUTTON_STYLES.find(b => b.id === id) || BUTTON_STYLES[0];

// ───────────────────────────── defaults / resolve ─────────────────────

export const DEFAULT_DESIGN = {
    themeId: DEFAULT_STORE_THEME_ID,
    fontPair: 'clean',
    radius: 'rounded',
    density: 'cozy',
    card: 'classic',
    cover: 'hero',
    texture: 'none',
    motion: 'calm',
    button: 'solid',
    vibe: null,
};

/**
 * Normalize whatever is stored (or nothing) into a complete config.
 * Accepts a full design_config object, OR a legacy bare theme_id string,
 * OR a store record (reads .design_config / .theme_id).
 */
export const resolveDesign = (input) => {
    let cfg = {};
    if (typeof input === 'string') {
        cfg = { themeId: input };
    } else if (input && typeof input === 'object') {
        if (input.design_config && typeof input.design_config === 'object') {
            cfg = { ...input.design_config };
            if (!cfg.themeId && input.theme_id) cfg.themeId = input.theme_id;
        } else if ('themeId' in input || 'fontPair' in input || 'radius' in input) {
            cfg = { ...input };
        } else if (input.theme_id) {
            cfg = { themeId: input.theme_id };
        }
    }
    const merged = { ...DEFAULT_DESIGN, ...cfg };
    // Guard every axis against stale / unknown ids.
    merged.themeId  = findStoreTheme(merged.themeId).id;
    merged.fontPair = findFontPair(merged.fontPair).id;
    merged.radius   = findRadius(merged.radius).id;
    merged.density  = findDensity(merged.density).id;
    merged.card     = findCard(merged.card).id;
    merged.cover    = findCover(merged.cover).id;
    merged.texture  = findTexture(merged.texture).id;
    merged.motion   = findMotion(merged.motion).id;
    merged.button   = findButton(merged.button).id;
    return merged;
};

// ───────────────────────────── VIBES ──────────────────────────────────
// One-tap personalities. Each composes a palette with every structural
// axis so a single tap can transform the store top to bottom.

export const VIBES = [
    {
        id: 'editorial-boutique',
        name: 'בוטיק עריכה',
        tagline: 'מגזין אופנה יוקרתי — שקט, מרווח, מוקפד',
        themeId: 'manhattan-penthouse', fontPair: 'editorial', radius: 'sharp',
        density: 'spacious', card: 'editorial', cover: 'split',
        texture: 'paper', motion: 'calm', button: 'sharp',
    },
    {
        id: 'neo-luxe',
        name: 'ניאו לוקס',
        tagline: 'זהב על כהה — סוויטת יוקרה אחרי החשכה',
        themeId: 'onyx-couture', fontPair: 'grand', radius: 'soft',
        density: 'spacious', card: 'framed', cover: 'hero',
        texture: 'mesh', motion: 'calm', button: 'outline',
    },
    {
        id: 'warm-artisan',
        name: 'מלאכת יד חמה',
        tagline: 'אופה שכונתי — נעים, ביתי, מזמין',
        themeId: 'cinnamon-bun', fontPair: 'soft', radius: 'rounded',
        density: 'cozy', card: 'classic', cover: 'banner',
        texture: 'paper', motion: 'lively', button: 'pill',
    },
    {
        id: 'brutalist-market',
        name: 'שוק ברוטליסטי',
        tagline: 'נועז, גרפי, ללא פשרות — סטייט­מנט',
        themeId: 'modern-tokyo', fontPair: 'bold', radius: 'sharp',
        density: 'compact', card: 'framed', cover: 'minimal',
        texture: 'grid', motion: 'lively', button: 'sharp',
    },
    {
        id: 'playful-pop',
        name: 'פופ שובב',
        tagline: 'צבעוני, אנרגטי, צעיר — כיף טהור',
        themeId: 'tie-dye', fontPair: 'statement', radius: 'pill',
        density: 'cozy', card: 'minimal', cover: 'hero',
        texture: 'dots', motion: 'lively', button: 'pill',
    },
    {
        id: 'zen-minimal',
        name: 'זן מינימלי',
        tagline: 'אוויר, שקט, מהות — פחות זה יותר',
        themeId: 'japanese-tea-house', fontPair: 'clean', radius: 'soft',
        density: 'spacious', card: 'minimal', cover: 'minimal',
        texture: 'none', motion: 'calm', button: 'outline',
    },
    {
        id: 'vintage-apothecary',
        name: 'בית מרקחת וינטג׳',
        tagline: 'תוויות עתיקות, נייר וזהב — סיפור של פעם',
        themeId: 'vintage-bookshop', fontPair: 'grand', radius: 'sharp',
        density: 'cozy', card: 'framed', cover: 'split',
        texture: 'paper', motion: 'calm', button: 'sharp',
    },
    {
        id: 'organic-earth',
        name: 'אדמה אורגנית',
        tagline: 'טבע, חורש זיתים, אור רך — נושם',
        themeId: 'olive-grove', fontPair: 'soft', radius: 'rounded',
        density: 'spacious', card: 'classic', cover: 'hero',
        texture: 'none', motion: 'calm', button: 'pill',
    },
    {
        id: 'midnight-gallery',
        name: 'גלריית חצות',
        tagline: 'יצירות תחת זרקור — דרמטי וכהה',
        themeId: 'midnight-sapphire', fontPair: 'editorial', radius: 'sharp',
        density: 'spacious', card: 'editorial', cover: 'hero',
        texture: 'mesh', motion: 'calm', button: 'outline',
    },
    {
        id: 'street-poster',
        name: 'פוסטר רחוב',
        tagline: 'הדפס גדול, אנרגיית פסטיבל — בלתי נשכח',
        themeId: 'bohemian-sunset', fontPair: 'poster', radius: 'sharp',
        density: 'compact', card: 'editorial', cover: 'hero',
        texture: 'grid', motion: 'lively', button: 'sharp',
    },
    {
        id: 'handwritten-cafe',
        name: 'קפה כתוב ביד',
        tagline: 'לוח גיר, חמים ואישי — כמו פתק מהשף',
        themeId: 'coffee-bean', fontPair: 'handcraft', radius: 'rounded',
        density: 'cozy', card: 'classic', cover: 'banner',
        texture: 'paper', motion: 'lively', button: 'pill',
    },
    {
        id: 'coastal-fresh',
        name: 'רעננות חופית',
        tagline: 'אור ים, פשתן ותכלת — קליל ונקי',
        themeId: 'mediterranean-linen', fontPair: 'clean', radius: 'pill',
        density: 'spacious', card: 'minimal', cover: 'split',
        texture: 'none', motion: 'calm', button: 'pill',
    },
];

export const findVibe = (id) => VIBES.find(v => v.id === id) || null;

/** Expand a Vibe into a full design_config (stamps `vibe` for highlight). */
export const vibeToConfig = (vibe) => {
    if (!vibe) return { ...DEFAULT_DESIGN };
    const { id, name, tagline, ...axes } = vibe;
    return resolveDesign({ ...axes, vibe: id });
};

/** True when the current config still matches every axis of its Vibe. */
export const matchesVibe = (config, vibe) => {
    if (!vibe) return false;
    const c = resolveDesign(config);
    return ['themeId', 'fontPair', 'radius', 'density', 'card', 'cover', 'texture', 'motion', 'button']
        .every(k => c[k] === vibe[k]);
};
